import { Component, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../core/auth/auth.service';
import { I18nService } from './i18n.service';
import { TranslatePipe } from './translate.pipe';
import { ThemeService } from './theme.service';
import { InstallPromptService } from './install-prompt.service';

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe],
  template: `
    <!-- Desktop top nav -->
    <header class="app-topnav">
      <a class="app-topnav-brand" routerLink="/dashboard"><img src="assets/logo.png" alt="Port of Bar"></a>
      <nav class="app-topnav-tabs">
        <a routerLink="/dashboard" routerLinkActive="active" [routerLinkActiveOptions]="{exact:true}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"/></svg>
          <span>{{ 'nav.dashboard' | t }}</span>
        </a>
        <a routerLink="/vessel-announcements" routerLinkActive="active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4" r="2"/><path d="M12 6v14"/><path d="M8 9h8"/><path d="M5 13a7 7 0 0 0 14 0"/></svg>
          <span>{{ 'nav.vesselAnnouncements' | t }}</span>
        </a>
        <a routerLink="/truck-announcements" routerLinkActive="active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="7" width="13" height="9" rx="1"/><path d="M14.5 10h3.5l3 3v3h-6.5z"/><circle cx="6" cy="18.5" r="1.6" fill="currentColor" stroke="none"/><circle cx="17.5" cy="18.5" r="1.6" fill="currentColor" stroke="none"/></svg>
          <span>{{ 'nav.truckAnnouncements' | t }}</span>
        </a>
        <a routerLink="/truck-announcement-pins" routerLinkActive="active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8" cy="11" r="2"/><path d="M5.3 16c.3-1.6 1.4-2.5 2.7-2.5s2.4.9 2.7 2.5"/><path d="M13.5 9.5h6"/><path d="M13.5 13h6"/><path d="M13.5 16h3.5"/></svg>
          <span>{{ 'nav.pinList' | t }}</span>
        </a>
        <button type="button" [class.active]="moreOpen()" (click)="toggleMore()">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="5" cy="12" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="19" cy="12" r="1.9"/></svg>
          <span>{{ 'nav.more' | t }}</span>
        </button>
      </nav>
      <div class="app-topnav-actions">
        <div class="lang-switch">
          <button type="button" [class.active]="i18n.lang() === 'en'" (click)="i18n.setLang('en')">EN</button>
          <button type="button" [class.active]="i18n.lang() === 'me'" (click)="i18n.setLang('me')">CG</button>
        </div>
        <div class="theme-switch">
          <button type="button" [class.active]="theme.mode() === 'light'" (click)="theme.setMode('light')" aria-label="Light theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'system'" (click)="theme.setMode('system')" aria-label="System theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16.5V20"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'dark'" (click)="theme.setMode('dark')" aria-label="Dark theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"/></svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Mobile bottom nav -->
    <nav class="app-bottomnav">
      <a routerLink="/dashboard" routerLinkActive="active" [routerLinkActiveOptions]="{exact:true}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"/></svg>
        <span>{{ 'nav.dashboard' | t }}</span>
      </a>
      <a routerLink="/vessel-announcements" routerLinkActive="active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4" r="2"/><path d="M12 6v14"/><path d="M8 9h8"/><path d="M5 13a7 7 0 0 0 14 0"/></svg>
        <span>{{ 'nav.vesselAnnouncements' | t }}</span>
      </a>
      <a routerLink="/truck-announcements" routerLinkActive="active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="7" width="13" height="9" rx="1"/><path d="M14.5 10h3.5l3 3v3h-6.5z"/><circle cx="6" cy="18.5" r="1.6" fill="currentColor" stroke="none"/><circle cx="17.5" cy="18.5" r="1.6" fill="currentColor" stroke="none"/></svg>
        <span>{{ 'nav.truckAnnouncements' | t }}</span>
      </a>
      <a routerLink="/truck-announcement-pins" routerLinkActive="active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8" cy="11" r="2"/><path d="M5.3 16c.3-1.6 1.4-2.5 2.7-2.5s2.4.9 2.7 2.5"/><path d="M13.5 9.5h6"/><path d="M13.5 13h6"/><path d="M13.5 16h3.5"/></svg>
        <span>{{ 'nav.pinList' | t }}</span>
      </a>
      <button type="button" [class.active]="moreOpen()" (click)="toggleMore()">
        <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="5" cy="12" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="19" cy="12" r="1.9"/></svg>
        <span>{{ 'nav.more' | t }}</span>
      </button>
    </nav>

    <!-- Shared "More" overlay -->
    <div class="more-backdrop" [class.open]="moreOpen()" (click)="closeMore()"></div>
    <div class="more-sheet" [class.open]="moreOpen()">
      <div class="more-sheet-grab"></div>
      <a routerLink="/work-orders" (click)="closeMore()">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><rect x="3" y="8" width="18" height="11" rx="1.5"/><path d="M3 13h18"/></svg>
        {{ 'nav.workOrderList' | t }}
      </a>
      <a routerLink="/service-requests" (click)="closeMore()">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 2.5h8l4 4v14a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-17a1 1 0 0 1 1-1z"/><path d="M14.5 2.5v4h4"/><path d="M8 12.5h8"/><path d="M8 16.5h8"/></svg>
        {{ 'nav.serviceRequests' | t }}
      </a>
      <a routerLink="/container-use" (click)="closeMore()">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7z"/><path d="M12 12v9.5"/><path d="M3.5 7 12 12l8.5-5"/></svg>
        {{ 'nav.containerUse' | t }}
      </a>
      <a routerLink="/permits" (click)="closeMore()">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="14" r="4"/><path d="M10.3 10.7 20 1"/><path d="M16 5l3 3"/><path d="M13.3 7.7l3 3"/></svg>
        {{ 'nav.permits' | t }}
      </a>

      <div class="drawer-divider more-sheet-mobile-only"></div>
      <div class="more-sheet-toggles more-sheet-mobile-only">
        <div class="lang-switch">
          <button type="button" [class.active]="i18n.lang() === 'en'" (click)="i18n.setLang('en')">EN</button>
          <button type="button" [class.active]="i18n.lang() === 'me'" (click)="i18n.setLang('me')">CG</button>
        </div>
        <div class="theme-switch">
          <button type="button" [class.active]="theme.mode() === 'light'" (click)="theme.setMode('light')" aria-label="Light theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'system'" (click)="theme.setMode('system')" aria-label="System theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16.5V20"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'dark'" (click)="theme.setMode('dark')" aria-label="Dark theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"/></svg>
          </button>
        </div>
      </div>

      @if (!install.isStandalone() && (install.canInstall() || install.isIOS)) {
        <div class="drawer-divider"></div>
        <button type="button" (click)="onInstallClick()">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2.5" width="16" height="19" rx="2"/><path d="M9 18h6"/><path d="M12 6v6"/><path d="M9 9l3 3 3-3"/></svg>
          {{ 'nav.installApp' | t }}
        </button>
        @if (showIosInstructions()) {
          <div class="install-ios-hint">
            <p>{{ 'nav.installIosSteps' | t }}</p>
            <button type="button" class="install-ios-hint-close" (click)="showIosInstructions.set(false)">{{ 'common.close' | t }}</button>
          </div>
        }
      }

      <div class="drawer-divider"></div>
      <p class="drawer-section-label">{{ 'nav.system' | t }} ({{ auth.userName() || 'User' }})</p>
      <button type="button" (click)="logout()">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/></svg>
        {{ 'nav.logOut' | t }}
      </button>
    </div>
  `
})
export class AppNavComponent {
  readonly moreOpen = signal(false);
  readonly showIosInstructions = signal(false);

  constructor(
    readonly auth: AuthService,
    readonly i18n: I18nService,
    readonly theme: ThemeService,
    readonly install: InstallPromptService,
    private readonly router: Router
  ) {}

  toggleMore(): void { this.moreOpen.update(v => !v); }
  closeMore(): void { this.moreOpen.set(false); }

  onInstallClick(): void {
    if (this.install.canInstall()) {
      void this.install.promptInstall();
    } else if (this.install.isIOS) {
      this.showIosInstructions.update(v => !v);
    }
  }

  logout(): void {
    this.closeMore();
    this.auth.signOut();
    void this.router.navigateByUrl('/login');
  }
}
